// CEFR can-do descriptors, French-localised, A1.1 → B1.2.
// Source: adapted from CEFR Companion Volume (2020) global descriptors.
// Distribution: 5–7 descriptors per sub-level. Order = teaching progression.

import type { CefrSubLevel } from './sample-today.ts';

export interface CanDoDescriptor {
  id: string;
  cefr: CefrSubLevel;
  /** Skill domain — used for routing progress signals from tracks. */
  domain: 'oral' | 'écrit' | 'compréhension' | 'interaction';
  text: string;
}

export const CAN_DOS: CanDoDescriptor[] = [
  // A1.1 — first contact
  { id: 'a11-greet', cefr: 'A1.1', domain: 'oral', text: 'Je peux dire bonjour, me présenter (nom, nationalité) et prendre congé.' },
  { id: 'a11-count', cefr: 'A1.1', domain: 'oral', text: 'Je peux compter jusqu’à 100 et donner mon numéro de téléphone.' },
  { id: 'a11-letters', cefr: 'A1.1', domain: 'oral', text: 'Je peux épeler mon nom et lire les lettres de l’alphabet norvégien (æ ø å inclus).' },
  { id: 'a11-yesno', cefr: 'A1.1', domain: 'interaction', text: 'Je peux répondre par oui / non / je ne sais pas / je ne comprends pas.' },
  { id: 'a11-family', cefr: 'A1.1', domain: 'oral', text: 'Je peux nommer les membres de ma famille proche.' },

  // A1.2 — daily basics
  { id: 'a12-cafe', cefr: 'A1.2', domain: 'interaction', text: 'Je peux commander une boisson ou une viennoiserie au café.' },
  { id: 'a12-direction', cefr: 'A1.2', domain: 'interaction', text: 'Je peux demander mon chemin et comprendre une réponse simple (à droite, à gauche, tout droit).' },
  { id: 'a12-time', cefr: 'A1.2', domain: 'compréhension', text: 'Je peux comprendre l’heure, les jours de la semaine et les mois.' },
  { id: 'a12-shopping', cefr: 'A1.2', domain: 'interaction', text: 'Je peux acheter au magasin : demander un prix, payer, dire merci.' },
  { id: 'a12-weather', cefr: 'A1.2', domain: 'oral', text: 'Je peux parler du temps qu’il fait (il pleut, il neige, il fait froid).' },

  // A1.3 — describing yourself
  { id: 'a13-routine', cefr: 'A1.3', domain: 'oral', text: 'Je peux décrire ma journée typique avec des verbes courants au présent.' },
  { id: 'a13-likes', cefr: 'A1.3', domain: 'oral', text: 'Je peux dire ce que j’aime et n’aime pas (jeg liker / jeg liker ikke).' },
  { id: 'a13-housing', cefr: 'A1.3', domain: 'oral', text: 'Je peux décrire où j’habite (appartement, maison, quartier).' },
  { id: 'a13-job', cefr: 'A1.3', domain: 'oral', text: 'Je peux dire ce que je fais dans la vie (profession ou études).' },
  { id: 'a13-form', cefr: 'A1.3', domain: 'écrit', text: 'Je peux remplir un formulaire administratif simple (nom, adresse, téléphone, fødselsnummer).' },

  // A1.4 — basic interactions
  { id: 'a14-introduce', cefr: 'A1.4', domain: 'interaction', text: 'Je peux présenter quelqu’un d’autre et poser des questions polies.' },
  { id: 'a14-restaurant', cefr: 'A1.4', domain: 'interaction', text: 'Je peux commander un repas complet au restaurant et demander l’addition.' },
  { id: 'a14-doctor', cefr: 'A1.4', domain: 'interaction', text: 'Je peux décrire un problème de santé simple (jeg har vondt i …).' },
  { id: 'a14-sms', cefr: 'A1.4', domain: 'écrit', text: 'Je peux écrire un SMS pour fixer un rendez-vous ou changer une heure.' },
  { id: 'a14-numbers', cefr: 'A1.4', domain: 'compréhension', text: 'Je peux comprendre des prix, des dates et des numéros de bus.' },

  // A2.1 — practical autonomy
  { id: 'a21-bank', cefr: 'A2.1', domain: 'interaction', text: 'Je peux gérer une démarche bancaire ou administrative simple en face-à-face.' },
  { id: 'a21-news', cefr: 'A2.1', domain: 'compréhension', text: 'Je peux comprendre l’essentiel d’un bulletin météo radio.' },
  { id: 'a21-past', cefr: 'A2.1', domain: 'oral', text: 'Je peux raconter ce que j’ai fait hier (preteritum / perfektum).' },
  { id: 'a21-plan', cefr: 'A2.1', domain: 'oral', text: 'Je peux parler de mes projets pour le week-end ou les vacances (skal / kommer til å).' },
  { id: 'a21-email', cefr: 'A2.1', domain: 'écrit', text: 'Je peux écrire un courriel court à un voisin ou à mon propriétaire.' },

  // A2.2 — social fluency
  { id: 'a22-opinion', cefr: 'A2.2', domain: 'oral', text: 'Je peux donner mon opinion sur un sujet familier (jeg synes …).' },
  { id: 'a22-story', cefr: 'A2.2', domain: 'oral', text: 'Je peux raconter une histoire courte avec début, milieu et fin.' },
  { id: 'a22-meeting', cefr: 'A2.2', domain: 'interaction', text: 'Je peux participer à un dugnad ou une réunion de voisinage informelle.' },
  { id: 'a22-instructions', cefr: 'A2.2', domain: 'compréhension', text: 'Je peux suivre les instructions d’une recette ou d’une notice de montage.' },
  { id: 'a22-feelings', cefr: 'A2.2', domain: 'oral', text: 'Je peux exprimer une émotion avec nuance (jeg gleder meg, jeg er skuffet).' },

  // A2.3 — descriptive depth
  { id: 'a23-experience', cefr: 'A2.3', domain: 'oral', text: 'Je peux décrire une expérience marquante (un voyage, un déménagement).' },
  { id: 'a23-compare', cefr: 'A2.3', domain: 'oral', text: 'Je peux comparer la France et la Norvège (climat, nourriture, habitudes).' },
  { id: 'a23-ads', cefr: 'A2.3', domain: 'compréhension', text: 'Je peux lire et comprendre une petite annonce (logement, emploi, finn.no).' },
  { id: 'a23-phone', cefr: 'A2.3', domain: 'interaction', text: 'Je peux gérer un appel téléphonique simple (prendre rendez-vous, demander une info).' },

  // A2.4 — workplace ready
  { id: 'a24-meeting', cefr: 'A2.4', domain: 'interaction', text: 'Je peux suivre une réunion courte sur un sujet familier et poser une question.' },
  { id: 'a24-cv', cefr: 'A2.4', domain: 'écrit', text: 'Je peux rédiger un CV et une lettre de motivation courte.' },
  { id: 'a24-news', cefr: 'A2.4', domain: 'compréhension', text: 'Je peux comprendre les titres et l’idée principale d’un article de NRK lett.' },
  { id: 'a24-explain', cefr: 'A2.4', domain: 'oral', text: 'Je peux expliquer comment fonctionne quelque chose de mon domaine.' },

  // B1.1 — independence emerging
  { id: 'b11-discussion', cefr: 'B1.1', domain: 'interaction', text: 'Je peux participer à une conversation sur des sujets familiers sans préparation.' },
  { id: 'b11-formal', cefr: 'B1.1', domain: 'écrit', text: 'Je peux écrire une lettre formelle pour une réclamation ou une demande.' },
  { id: 'b11-tv', cefr: 'B1.1', domain: 'compréhension', text: 'Je peux suivre l’essentiel d’une émission TV ou d’un podcast en bokmål standard.' },
  { id: 'b11-argument', cefr: 'B1.1', domain: 'oral', text: 'Je peux défendre brièvement mon point de vue avec des arguments simples.' },

  // B1.2 — confident user
  { id: 'b12-debate', cefr: 'B1.2', domain: 'interaction', text: 'Je peux participer à un débat informel sur un sujet d’actualité.' },
  { id: 'b12-essay', cefr: 'B1.2', domain: 'écrit', text: 'Je peux rédiger un texte d’une page sur un sujet familier avec une structure claire.' },
  { id: 'b12-dialect', cefr: 'B1.2', domain: 'compréhension', text: 'Je peux comprendre un locuteur siddis (Stavanger) parlant à vitesse normale sur un sujet courant.' },
  { id: 'b12-complain', cefr: 'B1.2', domain: 'interaction', text: 'Je peux gérer une situation imprévue (objet perdu, erreur de facture, retard de train).' },
];

export function canDosByLevel(level: CefrSubLevel): CanDoDescriptor[] {
  return CAN_DOS.filter((c) => c.cefr === level);
}

export const CEFR_ORDER: CefrSubLevel[] = [
  'A1.1', 'A1.2', 'A1.3', 'A1.4',
  'A2.1', 'A2.2', 'A2.3', 'A2.4',
  'B1.1', 'B1.2', 'B1.3', 'B1.4',
];

export function cefrIndex(level: CefrSubLevel): number {
  return CEFR_ORDER.indexOf(level);
}
